import type {
    DynamicFilterGroupValue,
    DynamicFilterValue,
} from "app/pages/sales-intelligence/domain/search/types/dynamic-filter-values";
import type { Translatable } from "app/pages/sales-intelligence/types";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import { inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { SearchFilterInstance } from "../types/searchFilterInstance";

import { FilterPanelBaseFilter } from "./BaseFilter";
import type { CompositeFilterModel } from "./CompositeFilterModel";

/**
 * Config-supplied mapping between member keys and per-condition inclusion sides (SI-8738): which side a member
 * key encodes, and which sibling member captures the opposite side of the same logical field. The key scheme
 * stays with the config — the framework never learns its shape.
 */
export type ConditionSides = {
    /** The inclusion side a member key encodes. */
    readonly inclusionOf: (memberKey: string) => InclusionOption;
    /** The member key capturing the given side of the same logical field (identity when sides are unsupported). */
    readonly variantKeyOf: (memberKey: string, inclusion: InclusionOption) => string;
};

const SIDELESS: ConditionSides = {
    inclusionOf: () => inclusionOptionMap.includeOnly,
    variantKeyOf: (memberKey) => memberKey,
};

export class DynamicGroupFilter
    extends FilterPanelBaseFilter<DynamicFilterGroupValue, unknown, DynamicFilterGroupValue>
    implements CompositeFilterModel<DynamicFilterGroupValue>
{
    readonly fields: readonly SearchFilterInstance[];

    private readonly conditionSides: ConditionSides;

    /** Group display name — shown as the dialog title (e.g. "Ownership"). */
    readonly title?: Translatable | string;

    constructor(
        fields: readonly SearchFilterInstance[],
        conditionSides?: ConditionSides,
        title?: Translatable | string,
    ) {
        super({ options: undefined, initialDtoValue: undefined });

        this.fields = fields;
        this.conditionSides = conditionSides ?? SIDELESS;
        this.title = title;
    }

    getFields(): readonly SearchFilterInstance[] {
        return this.fields;
    }

    /** The logical fields — one member per field, the include-side one. Use for pickers; `getFields` for values. */
    getBaseFields(): readonly SearchFilterInstance[] {
        return this.fields.filter(
            (field) => this.conditionSides.inclusionOf(field.key) === inclusionOptionMap.includeOnly,
        );
    }

    /** The inclusion side a member's condition encodes. */
    getConditionInclusion(memberKey: string): InclusionOption {
        return this.conditionSides.inclusionOf(memberKey);
    }

    /** The member capturing the given inclusion side of the same logical field, when the group registers one. */
    getVariant(memberKey: string, inclusion: InclusionOption): SearchFilterInstance | undefined {
        const variantKey = this.conditionSides.variantKeyOf(memberKey, inclusion);

        return this.fields.find((field) => field.key === variantKey);
    }

    getFieldKeys(): readonly string[] {
        return this.fields.map((field) => field.key);
    }

    /** Pick this group's member values out of a flat panel map into its fragment. */
    collect(flat: Readonly<Record<string, unknown>>): DynamicFilterGroupValue {
        const fragment: DynamicFilterGroupValue = {};

        for (const key of this.getFieldKeys()) {
            const value = flat[key];

            if (value !== undefined) {
                fragment[key] = value as DynamicFilterGroupValue[string];
            }
        }

        return fragment;
    }

    /** Spread this group's fragment back to its flat member-keyed entries. */
    expand(fragment?: DynamicFilterGroupValue): Readonly<Record<string, DynamicFilterValue>> {
        const flat: Record<string, DynamicFilterValue> = {};

        if (!fragment) {
            return flat;
        }

        for (const key of this.getFieldKeys()) {
            const value = fragment[key];

            if (!is.undefined(value)) {
                flat[key] = value;
            }
        }

        return flat;
    }

    /**
     * Merge one member's raw widget value into the fragment. Each member writes only its own key, so a field
     * change never clobbers a sibling; an empty value drops the member key (an all-empty group reads as cleared).
     */
    writeField(value: DynamicFilterGroupValue, field: SearchFilterInstance, raw: unknown): DynamicFilterGroupValue {
        const key = field.key;
        const fieldModel = field.filter;

        const next: DynamicFilterGroupValue = { ...value };

        const fieldValue = fieldModel.toApiValue(raw);

        if (is.undefined(fieldValue) || fieldModel.isValueEmpty(fieldValue)) {
            delete next[key];
        } else {
            next[key] = fieldValue as DynamicFilterGroupValue[string];
        }

        return next;
    }

    fromApiValue(value?: DynamicFilterGroupValue): DynamicFilterGroupValue {
        return value ?? {};
    }

    toApiValue(value?: DynamicFilterGroupValue): DynamicFilterGroupValue | undefined {
        if (!value) {
            return undefined;
        }

        const next: DynamicFilterGroupValue = {};

        for (const field of this.fields) {
            const fieldValue = value[field.key];

            if (is.undefined(fieldValue) || field.filter.isValueEmpty(fieldValue)) {
                continue;
            }

            next[field.key] = fieldValue;
        }

        return Object.keys(next).length ? next : undefined;
    }

    isValueEmpty(value?: DynamicFilterGroupValue): boolean {
        return !value || this.fields.every((field) => field.filter.isValueEmpty(value[field.key]));
    }
}
